import { watch, onMounted, onUnmounted } from 'vue';
import type { Ref } from 'vue';

const KEYFRAME_AT_RULES = new Set(['keyframes', '-webkit-keyframes', '-moz-keyframes']);
const UNSCOPABLE_AT_RULES = new Set(['font-face', 'page', 'import', 'charset', 'namespace']);

const ROOT_SELECTOR = /^(html|body|:root)$/i;

const isWhitespace = (char: string): boolean => /\s/.test(char);

const scopeSelectorList = (selectorList: string, scope: string): string =>
  selectorList
    .split(',')
    .map((selector) => selector.trim())
    .filter(Boolean)
    .map((selector) => (ROOT_SELECTOR.test(selector) ? scope : `${scope} ${selector}`))
    .join(', ');

const skipComment = (css: string, pos: number): number => {
  const end = css.indexOf('*/', pos + 2);
  return end === -1 ? css.length : end + 2;
};

const readPrelude = (
  css: string,
  start: number,
): { prelude: string; index: number; hasBlock: boolean } => {
  let parensDepth = 0;
  let index = start;

  while (index < css.length && css[index] !== '}' && !(css[index] === '{' && parensDepth === 0)) {
    if (css[index] === '(') parensDepth++;
    if (css[index] === ')') parensDepth--;
    index++;
  }

  return {
    prelude: css.slice(start, index).trim(),
    index,
    hasBlock: css[index] === '{',
  };
};

const readBlock = (css: string, start: number): { content: string; nextIndex: number } => {
  let depth = 1;
  let index = start;

  while (index < css.length && depth > 0) {
    if (css[index] === '{') depth++;
    if (css[index] === '}') depth--;
    index++;
  }

  return { content: css.slice(start, index - 1), nextIndex: index };
};

const scopeCss = (css: string, scope: string): string => {
  let index = 0;
  const length = css.length;

  const parseRules = (): string => {
    let output = '';

    while (index < length) {
      while (index < length && isWhitespace(css[index]!)) index++;
      if (index >= length || css[index] === '}') break;

      if (css.startsWith('/*', index)) {
        index = skipComment(css, index);
        continue;
      }

      const { prelude, index: afterPrelude, hasBlock } = readPrelude(css, index);
      index = afterPrelude;

      if (!hasBlock) {
        index++;
        continue;
      }

      index++;

      const atRuleName = prelude.match(/^@([a-zA-Z-]+)/)?.[1]?.toLowerCase();

      if (
        atRuleName &&
        (KEYFRAME_AT_RULES.has(atRuleName) || UNSCOPABLE_AT_RULES.has(atRuleName))
      ) {
        const { content, nextIndex } = readBlock(css, index);
        index = nextIndex;
        output += `${prelude} {${content}}`;
        continue;
      }

      if (atRuleName) {
        const innerCss = parseRules();
        index++;
        output += `${prelude} {${innerCss}}`;
        continue;
      }

      const { content: declarations, nextIndex } = readBlock(css, index);
      index = nextIndex;
      output += `${scopeSelectorList(prelude, scope)} {${declarations}}`;
    }

    return output;
  };

  return parseRules();
};

export const useCustomCode = (
  containerEl: Ref<HTMLElement | null>,
  uid: string,
  html: Ref<string | undefined>,
  css: Ref<string | undefined>,
  js: Ref<string | undefined>,
) => {
  const scope = `#${uid}`;

  let styleEl: HTMLStyleElement | null = null;
  let scriptEl: HTMLScriptElement | null = null;

  const applyCss = (value: string | undefined) => {
    if (!value) {
      styleEl?.remove();
      styleEl = null;
      return;
    }

    if (!styleEl) {
      styleEl = document.createElement('style');
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = scopeCss(value, scope);
  };

  const render = () => {
    const container = containerEl.value;

    if (!container) {
      return;
    }

    container.innerHTML = html.value ?? '';

    scriptEl = null;

    if (js.value) {
      scriptEl = document.createElement('script');
      scriptEl.textContent = `(function () { ${js.value} })();`;
      container.appendChild(scriptEl);
    }
  };

  onMounted(() => {
    applyCss(css.value);
    render();
  });

  watch(css, applyCss);
  watch([html, js], render);

  onUnmounted(() => {
    styleEl?.remove();
    styleEl = null;
    scriptEl = null;
  });
};
